import type { FC } from 'react';
import { memo, useEffect, useMemo, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming
} from 'react-native-reanimated';

import { PostsRoute } from '@/features/posts/navigation/postsRoute';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { OverlayLink } from '@/navigation/components/OverlayLink';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export const TICKER_HEIGHT = 36;
const MS_PER_PX = 25;
const SEPARATOR_SPACING = 16;
const TICKER_FONT_SIZE = 13;
const TITLE_FALLBACK_TEASER_LENGTH = 75;
const TEASER_MAX_LENGTH = 120;
const LABEL_MAX_LENGTH = 90;
const LABEL_MAX_WIDTH_RATIO = 0.75;
const TITLE_ONLY_THRESHOLD = 5;
const MAX_REPEATS = 10;
const TITLE_TEXT_SEPARATOR = ' — ';

type DisplayMode = 'title' | 'teaser' | 'full';

const pickDisplayMode = (postCount: number): DisplayMode => {
  if (postCount <= 1) return 'full';
  if (postCount < TITLE_ONLY_THRESHOLD) return 'teaser';
  return 'title';
};

const truncate = (input: string, max: number): string =>
  input.length > max ? `${input.slice(0, max)}…` : input;

const normalizeWhitespace = (input: string | undefined): string =>
  input?.replaceAll(/\s+/g, ' ').trim() ?? '';

const buildLabel = (post: GenericPostDetail, mode: DisplayMode): string => {
  const title = normalizeWhitespace(post.title);
  const text = normalizeWhitespace(post.text);

  if (mode === 'full') {
    if (title && text) return `${title}${TITLE_TEXT_SEPARATOR}${text}`;
    return title || text;
  }

  if (mode === 'teaser') {
    if (title && text)
      return `${title}${TITLE_TEXT_SEPARATOR}${truncate(text, TEASER_MAX_LENGTH)}`;
    if (title) return title;
    return truncate(text, TEASER_MAX_LENGTH);
  }

  if (title) return title;
  return truncate(text, TITLE_FALLBACK_TEASER_LENGTH);
};

type StripProps = {
  posts: GenericPostDetail[];
  mode: DisplayMode;
  color: string;
  repeats?: number;
  maxLabelWidth?: number;
  onMeasured?: (width: number) => void;
};

const TickerStrip: FC<StripProps> = memo(function TickerStrip({
  posts,
  mode,
  color,
  repeats = 1,
  maxLabelWidth,
  onMeasured
}) {
  const onLayout = onMeasured
    ? (event: LayoutChangeEvent) => onMeasured(event.nativeEvent.layout.width)
    : undefined;
  const repeatedPosts = Array.from({ length: repeats }, () => posts).flat();

  return (
    <View style={styles.strip} onLayout={onLayout}>
      {repeatedPosts.map((post, index) => {
        const fullLabel = buildLabel(post, mode);
        const label = truncate(fullLabel, LABEL_MAX_LENGTH);
        // oxlint-disable-next-line unicorn/no-null
        if (!label) return null;
        return (
          <View style={styles.item} key={`${post.postId}-${index}`}>
            {/* eslint-disable-next-line react-native-a11y/has-accessibility-hint */}
            <OverlayLink
              screen={PostsRoute.PostDetail}
              params={{ id: post.postId }}
              accessibilityLabel={label}
              style={[
                styles.link,
                {
                  maxWidth: maxLabelWidth
                }
              ]}
            >
              <Text
                allowFontScaling={false}
                ellipsizeMode="tail"
                style={[styles.title, { color, maxWidth: maxLabelWidth }]}
                numberOfLines={1}
              >
                {label}
              </Text>
            </OverlayLink>
            <Text
              allowFontScaling={false}
              ellipsizeMode="clip"
              style={[styles.dot, { color }]}
              numberOfLines={1}
            >
              ·
            </Text>
          </View>
        );
      })}
    </View>
  );
});

export const RegionNewsTicker: FC<{
  posts?: GenericPostDetail[];
  isSuccess: boolean;
}> = ({ posts, isSuccess }) => {
  const theme = useAppTheme();

  const translateX = useSharedValue(0);
  const [baseStripWidth, setBaseStripWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const displayMode = useMemo(
    () => pickDisplayMode(posts?.length ?? 0),
    [posts?.length]
  );
  const maxLabelWidth =
    containerWidth > 0 ? containerWidth * LABEL_MAX_WIDTH_RATIO : undefined;

  const repeats = useMemo(() => {
    if (containerWidth <= 0 || baseStripWidth <= 0) return 1;
    return Math.min(
      Math.ceil(containerWidth / baseStripWidth) + 1,
      MAX_REPEATS
    );
  }, [baseStripWidth, containerWidth]);

  useEffect(() => {
    const animationWidth = baseStripWidth * repeats;
    if (animationWidth <= 0) return;
    translateX.value = 0;
    translateX.value = withRepeat(
      withTiming(-animationWidth, {
        duration: animationWidth * MS_PER_PX,
        easing: Easing.linear
      }),
      -1,
      false
    );
    return () => cancelAnimation(translateX);
  }, [baseStripWidth, repeats, translateX]);

  const animatedStyle = useAnimatedStyle(() => {
    'worklet';
    return { transform: [{ translateX: translateX.value }] };
  });

  // oxlint-disable-next-line unicorn/no-null
  if (!isSuccess || !posts || posts.length === 0) return null;

  return (
    <View
      testID="region-news-ticker"
      style={[styles.bar, { backgroundColor: theme.colors.primary }]}
      onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}
    >
      <Animated.View style={[styles.track, animatedStyle]}>
        <TickerStrip
          posts={posts}
          mode={displayMode}
          color={theme.colors.onPrimary}
          repeats={repeats}
          maxLabelWidth={maxLabelWidth}
        />
        <TickerStrip
          posts={posts}
          mode={displayMode}
          color={theme.colors.onPrimary}
          repeats={repeats}
          maxLabelWidth={maxLabelWidth}
        />
      </Animated.View>
      <View style={styles.measurement} pointerEvents="none">
        <TickerStrip
          posts={posts}
          mode={displayMode}
          color={theme.colors.onPrimary}
          maxLabelWidth={maxLabelWidth}
          onMeasured={setBaseStripWidth}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    height: TICKER_HEIGHT,
    overflow: 'hidden',
    justifyContent: 'center'
  },
  track: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start'
  },
  strip: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    overflow: 'hidden'
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    overflow: 'hidden'
  },
  link: {
    flexShrink: 0,
    overflow: 'hidden'
  },
  title: {
    flexShrink: 0,
    fontSize: TICKER_FONT_SIZE,
    lineHeight: TICKER_HEIGHT,
    overflow: 'hidden'
  },
  dot: {
    flexShrink: 0,
    fontSize: TICKER_FONT_SIZE,
    fontWeight: '700',
    lineHeight: TICKER_HEIGHT,
    marginHorizontal: SEPARATOR_SPACING
  },
  measurement: {
    opacity: 0,
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: -1
  }
});
